import React, { useState, useEffect, useMemo } from "react";
import { useCMTag } from "api/api";
import moment from "moment";
import Card from "../cards/Card";
import { stdTimeOptions, weekTimeOption } from "utility/timeOptions";
import { Thermal_Change, TimeOption } from "model/Datatypes";
import TickValue from "components/basic/TickValue";
import { ResponsiveContainer, LineChart, XAxis, Line, YAxis } from "recharts";

type Props = {
  tags: Thermal_Change;
};

const ThermalChangeCard = ({ tags }: Props) => {
  const tempTag = useMemo(() => tags.thermal_storage_temp, [tags]);

  const [time, setTime] = useState<TimeOption>(weekTimeOption);
  const [temperatureChagne, setTemperatureChagne] = useState<null | number>(null);
  const [currentTemp, setCurrentTemp] = useState<null | { time: number; value: number }>(null);
  const { cmData: tempOverTime, loading } = useCMTag(
    tempTag,
    time.from,
    time.to,
    time.groupingsType,
    true
  );
  const { cmData: curTemp, loading: curtempLoading } = useCMTag(
    tempTag,
    time.to - 360000,
    time.to,
    "MINUTES",
    true
  );

  const [graphData, setgraphData] = useState<{ time: number; temp: number }[]>([]);

  useEffect(() => {
    if (tempOverTime) {
      const startTemp = tempOverTime.points[0].value;
      const endTemp = tempOverTime.points[tempOverTime.points.length - 1].value;
      setTemperatureChagne(endTemp - startTemp);

      //prepare to graph...
      const cleaned = tempOverTime.points.map((p) => ({
        // time: moment(p.time).format("DD/MM"),
        time: p.time,
        temp: p.value,
      }));
      setgraphData(cleaned);
    }
  }, [tempOverTime]);

  useEffect(() => {
    if (curTemp) {
      const temp = curTemp.points[curTemp.points.length - 1];
      setCurrentTemp(temp);
    }
  }, [curTemp]);

  return (
    <Card
      timeOptions={stdTimeOptions}
      onNewTimeSelected={(newTime) => {
        setTime(newTime);
      }}
      timeSelected={time}
      loading={loading || curtempLoading}
    >
      <div className="relative text-gray-500 w-full h-full px-4 pt-4">
        <div className="text-xs">Current temperature</div>
        <div className="text-xl text-cyan font-bold mb-1">
          {currentTemp ? currentTemp.value.toFixed(1) : ".."}°C
        </div>
        <div className="text-xs">Change</div>
        <div className="text-sm font-medium leading-tight">
          {temperatureChagne ? `${temperatureChagne >= 0 ? "+" : ""}` : "..."}
          {temperatureChagne && <TickValue value={temperatureChagne} toFixed={1} />}
          °C
        </div>
        <div className="absolute bottom-0 -mb-2 right-0 mr-6 w-4/5 h-32">
          {graphData.length > 0 && (
            <ThermalGraph data={graphData} groupingType={time.groupingsType} />
          )}
        </div>
      </div>
    </Card>
  );
};

const ThermalGraph: React.FC<{
  data: { time: number; temp: number }[];
  groupingType: TimeOption["groupingsType"];
}> = ({ data, groupingType }) => {
  const third = data.length / 3;
  const xAxisTicks = [
    data[0].time,
    data[Math.round(third)].time,
    data[Math.round(2 * third)].time,
    data[data.length - 1].time,
  ];

  const momentFromat = groupingType === "MINUTES" ? "HH:mm" : "DD/MM";
  return (
    <ResponsiveContainer>
      <LineChart data={data} className={"overflow-svg-visible"}>
        <XAxis
          dataKey="time"
          ticks={xAxisTicks}
          tickFormatter={(val) => moment(val).format(momentFromat)}
          interval={0}
        />
        <YAxis dataKey="temp" tickFormatter={(val) => `${val}°c`} />
        <Line type="monotone" dataKey="temp" stroke="#2FD2A6" strokeWidth={3} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default ThermalChangeCard;
